<template>
  <div class="modal fade" ref="modalRef" tabindex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="true">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <font-awesome-icon :icon="['fas', 'cart-shopping']" class="me-1" />
            {{ $t('page.item.openOrders.title') }}
            <br>
            <span v-if="item" class="ms-2">
              <ItemField :item="item" />
            </span>
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="draftQuantities.length > 0" class="mb-3">
            <RouterLink :to="{ name: 'order-create' }" class="text-decoration-none" :title="$t('page.item.draftCart.title')" @click="hide()">
              <span class="text-primary me-2">
                <font-awesome-icon :icon="['fas', 'cart-shopping']" class="me-1" />
                {{ $t('page.item.draftCart.title') }}
              </span>
              <span v-for="entry in draftQuantities" :key="entry.unitCode" class="badge bg-warning text-dark ms-1">
                {{ entry.quantity }} {{ entry.unitCode }}
              </span>
            </RouterLink>
          </div>
          <div v-if="isLoading" class="loading">{{ $t('status.loadingEllipsis') }}</div>
          <div v-else-if="openOrders.length === 0 && draftQuantities.length === 0">{{ $t('error.noData') }}</div>
          <table v-else-if="openOrders.length > 0" class="table table-borderless table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>{{ $t('field.generic.order') }}</th>
                <th>{{ $t('field.generic.status') }}</th>
                <th>{{ $t('field.generic.supplier') }}</th>
                <th>{{ $t('field.order.deliveryDate') }}</th>
                <th class="text-end">{{ $t('page.item.openOrders.openQuantity') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in openOrders" :key="`${entry.orderId}-${entry.line.lineItemId}`">
                <td>
                  <RouterLink :to="{ name: 'order', params: { orderId: entry.orderId } }" class="btn btn-link text-start ps-0 py-0" @click="hide()">
                    {{ entry.orderCode }}
                  </RouterLink>
                  <span v-if="entry.line.isReplacement" class="badge bg-warning text-dark ms-1" :title="$t('page.item.openOrders.replacementOf', { code: entry.line.lineItemCode, name: entry.line.lineItemName })">
                    <font-awesome-icon :icon="['fas', 'right-left']" fixed-width />
                    {{ entry.line.lineItemCode }}
                  </span>
                </td>
                <td>
                  <OrderStatus :status="entry.status" />
                </td>
                <td class="text-melodi">{{ entry.supplierName }}</td>
                <td>
                  <span v-if="entry.deliveryDate">{{ $d(new Date(entry.deliveryDate), 'dateonly') }}</span>
                </td>
                <td class="text-end">
                  <span class="fw-semibold">{{ entry.line.openQuantity }}</span>
                  <span class="text-muted"> / {{ entry.line.quantity }} {{ entry.line.unitName }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Modal } from "bootstrap";
import { useItemsStore, useOrdersStore } from "@/stores";
import OrderStatus from "@/components/orders/OrderStatus.vue";
import { ItemField } from "@/components/fields";
import Item from "@/models/frontend/Item";
import type { IItemOpenOrder } from "@/models/responses/IItemOpenOrder";

const itemsStore = useItemsStore();
const ordersStore = useOrdersStore();
const modalRef = ref<HTMLElement | null>(null);
const openOrders = ref<IItemOpenOrder[]>([]);
const isLoading = ref(false);
const item = ref<Item | null>(null);

const draftQuantities = computed(() => {
  if (!item.value) return [];
  return ordersStore.draftQuantityByItemId[item.value.id] ?? [];
});

const open = async (target: Item) => {
  item.value = target;
  openOrders.value = [];
  isLoading.value = true;
  if (modalRef.value) {
    Modal.getOrCreateInstance(modalRef.value).show();
  }
  try {
    const [orders] = await Promise.all([
      itemsStore.fetchOpenOrders(target.id),
      ordersStore.fetchDraftLines(),
    ]);
    openOrders.value = orders;
  } catch (error) {
    console.error("Failed to load open orders for item", error);
    openOrders.value = [];
  } finally {
    isLoading.value = false;
  }
};

const hide = () => {
  if (modalRef.value) {
    Modal.getInstance(modalRef.value)?.hide();
  }
};

defineExpose({ open });
</script>
